import {
  Container,
  Button,
  Header,
  Grid,
  SpaceBetween,
  FormField,
  Input,
} from '@cloudscape-design/components';
import { ViewLayout } from '../../components/ViewLayout';
import { memo, useEffect, useEffectEvent, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  GOOGLE_PIPELINE_TASKS,
  IssueGroup,
  LoadedPlaceReport,
  PlacePeriodReport,
  PlaceReport,
  PlaceSourceSnapshot,
  StatisticStatus,
  Task,
} from '../../common/types';
import {
  sortTasks as sortTasksFn,
  computeHasGooglePeriods,
  computeStatus,
  computeCurrentTask,
  computeSourceVisibility,
  computeSourceAvailabilityMap,
  periodMatchesSource,
} from '../../common/cardLogic';
import type { SourceAvailability } from '../../common/cardLogic';
import { StatisticBox } from '../../components/StatisticBox';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getReport } from '../../common/api/reports';
import { LoadingOrError } from '../../components/Loading';
import ReportCardHeader from '../../components/ReportCardHeader';
import { ReviewList } from '../../components/ReviewList';
import { StringParam, useQueryParam, withDefault } from 'use-query-params';
import { KeywordList } from '../../components/KeywordList';
import { ScorecardHeader } from '../../components/ScorecardHeader';
import { ReportDownloadButton } from '../../components/ReportDownloadButton';
import { PublicScorecardSendModal } from '../../components/PublicScorecardSendModal';
import {
  ScorecardLoadingView,
  ScorecardLoadingReport,
} from '../../components/ScorecardLoadingView';
import { GoogleIcon } from '../../common/icons/GoogleIcon';
import { YelpIcon } from '../../common/icons/YelpIcon';
import { AIIcon } from '../../common/icons/AIIcon';
import { IssueGroupView } from '../../components/IssueGroupView';
import { BBBIcon } from '../../common/icons/BBBIcon';
import { DealerRaterIcon } from '../../common/icons/DealerRaterIcon';
import { OpenTableIcon } from '../../common/icons/OpenTableIcon';
import { TripAdvisorIcon } from '../../common/icons/TripAdvisorIcon';
import { CarsComIcon } from '../../common/icons/CarsComIcon';
import { CarFaxIcon } from '../../common/icons/CarFaxIcon';
import { EdmundsIcon } from '../../common/icons/EdmundsIcon';
import { CarGurusIcon } from '../../common/icons/CarGurusIcon';
import { BookingIcon } from '../../common/icons/BookingIcon';
import { ExpediaIcon } from '../../common/icons/ExpediaIcon';
import { GolfPassIcon } from '../../common/icons/GolfPassIcon';
import { SourceRatingsSummary } from '../../components/SourceRatingsSummary';
import { CompetitorSection } from '../../components/CompetitorSection';
import { ViolationsSummaryBadge } from '../../components/ViolationsSummaryBadge';
import { ScorecardSourceTabs } from '../../components/ScorecardSourceTabs';
import { ScorecardOpportunities } from '../../components/ScorecardOpportunities';
import { getRatingTrend, getScorecardOpportunities } from '../../common/scorecardInsights';
import { getPlaceSourceData } from '../../common/scorecardSourceData';
import {
  formatCustomerVolumeInput,
  validateCustomerVolumeInput,
} from '../../common/customerVolume';
import {
  CALENDAR_PERIOD_OPTIONS,
  CalendarPeriodControl,
} from '../../components/CalendarPeriodControl';

export default function View() {
  const { uuid } = useParams({ from: '/$uuid/' });
  const navigate = useNavigate();
  const [customerVolumeParam, setCustomerVolumeParam] = useQueryParam(
    'customer_volume',
    StringParam,
  );
  const [customerVolumeInput, setCustomerVolumeInput] = useState(
    formatCustomerVolumeInput(customerVolumeParam ?? ''),
  );
  const [debouncedCustomerVolume, setDebouncedCustomerVolume] = useState(
    validateCustomerVolumeInput(customerVolumeParam ?? undefined).customerVolume,
  );
  const initializedFromReport = useRef(false);
  const customerVolumeValidation = validateCustomerVolumeInput(customerVolumeInput);

  useEffect(() => {
    if (!customerVolumeInput || customerVolumeValidation.error) return;
    const timeout = window.setTimeout(
      () => setDebouncedCustomerVolume(customerVolumeValidation.customerVolume),
      400,
    );
    return () => window.clearTimeout(timeout);
  }, [customerVolumeInput, customerVolumeValidation.customerVolume, customerVolumeValidation.error]);

  const getQuery = useQuery({
    queryKey: ['reports', uuid, debouncedCustomerVolume],
    queryFn: () => getReport(uuid ?? '', debouncedCustomerVolume),
    enabled: !!uuid,
    // 5 minutes
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    placeholderData: (previous) => previous,
  });

  useEffect(() => {
    if (
      !initializedFromReport.current &&
      customerVolumeParam === undefined &&
      getQuery.data?.customer_volume != null
    ) {
      initializedFromReport.current = true;
      setCustomerVolumeInput(formatCustomerVolumeInput(String(getQuery.data.customer_volume)));
    }
  }, [customerVolumeParam, getQuery.data?.customer_volume]);

  const loading =
    getQuery.isLoading || (getQuery.data && !!getQuery.data.slug && getQuery.data.slug !== uuid);

  useEffect(() => {
    if (getQuery.data?.slug && getQuery.data.slug !== uuid) {
      navigate({ to: `/${getQuery.data.slug}${window.location.search}`, replace: true });
      // no need to navigate, can we just update the url without reloading the page
      // can I maintain the query params?
      // window.history.replaceState({}, '', `/${getQuery.data.slug}${window.location.search}`);
    }
  }, [getQuery.data, uuid]);

  return (
    <ViewLayout
      header={
        <ReportCardHeader
          user={getQuery.data?.user ?? undefined}
          report={getQuery.data ?? undefined}
        />
      }
      contentType="dashboard"
      navigationHide
      headerVariant="high-contrast"
    >
      {loading || !getQuery.data || getQuery.error ? (
        <LoadingOrError loading={loading} retry={() => getQuery.refetch()} error={getQuery.error} />
      ) : (
        <CardTaskView
          report={getQuery.data}
          customerVolume={debouncedCustomerVolume}
          customerVolumeInput={customerVolumeInput}
          customerVolumeError={customerVolumeValidation.error}
          onCustomerVolumeChange={(value) => {
            const formattedValue = formatCustomerVolumeInput(value);
            setCustomerVolumeInput(formattedValue);
            const validation = validateCustomerVolumeInput(formattedValue);
            if (validation.customerVolume !== undefined) {
              setCustomerVolumeParam(String(validation.customerVolume));
            } else {
              setCustomerVolumeParam(value || undefined);
              setDebouncedCustomerVolume(undefined);
            }
          }}
        />
      )}
    </ViewLayout>
  );
}

export type CardTaskViewProps = {
  report: PlaceReport;
  customerVolume?: number;
  customerVolumeInput?: string;
  customerVolumeError?: string;
  onCustomerVolumeChange?: (value: string) => void;
};

export function CardTaskView(props: CardTaskViewProps) {
  const [report, setReport] = useState(props.report);
  const getReq = useMutation({
    mutationKey: ['reports', 'background-poll'],
    meta: { ignoreNetworkErrors: true },
    mutationFn: () => getReport(props.report.uuid, props.customerVolume),
    onSuccess: (data) => {
      // simple comparison to see if we need to update the session
      // TODO better comparison
      if (data && JSON.stringify(report) !== JSON.stringify(data)) {
        setReport(data);
      }
    },
  });

  useEffect(() => setReport(props.report), [props.report]);

  const currentTask = useMemo(() => computeCurrentTask(report.tasks), [report.tasks]);

  // Check if Google source has periods ready - this is the primary source
  // Treat missing/null source as 'google' for backwards compatibility
  const hasGooglePeriods = useMemo(() => computeHasGooglePeriods(report.periods), [report.periods]);

  const status = useMemo(
    () => computeStatus(report.tasks, hasGooglePeriods),
    [report.tasks, hasGooglePeriods],
  );

  const pollReport = useEffectEvent(() => {
    if (status === 'success' || document.hidden || navigator.onLine === false || getReq.isPending) {
      return;
    }

    void getReq.mutateAsync().catch(() => undefined);
  });

  useEffect(() => {
    if (status === 'success') {
      return;
    }

    // Poll more frequently (2s) to get progress updates during review collection
    const pollInterval = setInterval(pollReport, 2000);

    return () => clearInterval(pollInterval);
  }, [status]);

  if (status === 'success' && report.place !== undefined && hasGooglePeriods) {
    return (
      <CardView
        report={report as LoadedPlaceReport}
        onRefresh={() => getReq.mutateAsync()}
        customerVolume={props.customerVolume}
        customerVolumeInput={props.customerVolumeInput ?? String(report.customer_volume ?? '')}
        customerVolumeError={props.customerVolumeError}
        onCustomerVolumeChange={props.onCustomerVolumeChange ?? (() => undefined)}
      />
    );
  }

  return (
    <ScorecardLoadingView
      report={report as ScorecardLoadingReport}
      currentTask={currentTask}
      status={status}
    />
  );
}

export type CardViewProps = {
  report: LoadedPlaceReport;
  onRefresh?: () => Promise<unknown>;
  customerVolume?: number;
  customerVolumeInput?: string;
  customerVolumeError?: string;
  onCustomerVolumeChange?: (value: string) => void;
};

// TODO revert to 180-days when google pipeline is fixed
const RangeFiltersParam = withDefault(StringParam, '180-days');
// const RangeFiltersParam = withDefault(StringParam, '30-days');
const SourceFiltersParam = withDefault(StringParam, 'google');

const rangeIdToDays: Record<string, number> = {
  '30-days': 30,
  '60-days': 60,
  '90-days': 90,
  '180-days': 180,
};

type ReportSource =
  | 'google'
  | 'yelp'
  | 'bbb'
  | 'dealerrater'
  | 'opentable'
  | 'tripadvisor'
  | 'carscom'
  | 'carfax'
  | 'edmunds'
  | 'cargurus'
  | 'booking'
  | 'expedia'
  | 'golfpass'
  | 'ai';

type SourceOption = {
  text: string;
  id: ReportSource;
  iconSvg: ReactNode;
  availability: SourceAvailability;
  disabled: boolean;
  disabledReason?: string;
};

export function CardView({
  report,
  onRefresh,
  customerVolume,
  customerVolumeInput = String(report.customer_volume ?? ''),
  customerVolumeError,
  onCustomerVolumeChange = () => undefined,
}: CardViewProps) {
  const [sendModalVisible, setSendModalVisible] = useState(false);

  // Build snapshot map for O(1) lookups by source
  const snapshotMap = useMemo(() => {
    const map = new Map<string, PlaceSourceSnapshot>();
    for (const s of report.source_snapshots ?? []) {
      map.set(s.source, s);
    }
    return map;
  }, [report.source_snapshots]);

  const {
    isAutomotive,
    hasDealerRater,
    hasCarsCom,
    hasCarFax,
    hasEdmunds,
    hasCarGurus,
    isRestaurant,
    hasOpenTable,
    isHotel,
    isGolf,
    hasTripAdvisor,
    hasBooking,
    hasExpedia,
    hasGolfPass,
  } = useMemo(
    () =>
      computeSourceVisibility(report.business_type, report.place, report.source_snapshots ?? []),
    [report.business_type, report.place, report.source_snapshots],
  );

  const ALL_SOURCE_OPTIONS = useMemo(() => {
    const options = [
      { text: 'Google', id: 'google' as ReportSource, iconSvg: <GoogleIcon /> },
      { text: 'Yelp', id: 'yelp' as ReportSource, iconSvg: <YelpIcon /> },
      { text: 'BBB', id: 'bbb' as ReportSource, iconSvg: <BBBIcon /> },
    ];
    // Add DealerRater if the business has a dealerrater_link or is automotive
    if (hasDealerRater) {
      options.push({
        text: 'DealerRater',
        id: 'dealerrater' as ReportSource,
        iconSvg: <DealerRaterIcon />,
      });
    }
    // Add Cars.com if the business has a carscom_link or is automotive
    if (hasCarsCom) {
      options.push({ text: 'Cars.com', id: 'carscom' as ReportSource, iconSvg: <CarsComIcon /> });
    }
    // Add CarFax if the business has a carfax_link or is automotive
    if (hasCarFax) {
      options.push({ text: 'CarFax', id: 'carfax' as ReportSource, iconSvg: <CarFaxIcon /> });
    }
    // Add Edmunds if the business has an edmunds_link or is automotive
    if (hasEdmunds) {
      options.push({ text: 'Edmunds', id: 'edmunds' as ReportSource, iconSvg: <EdmundsIcon /> });
    }
    // Add CarGurus if the business has a cargurus_link or is automotive
    if (hasCarGurus) {
      options.push({ text: 'CarGurus', id: 'cargurus' as ReportSource, iconSvg: <CarGurusIcon /> });
    }
    // Add OpenTable if the business has an opentable_link or is a restaurant
    if (hasOpenTable) {
      options.push({
        text: 'OpenTable',
        id: 'opentable' as ReportSource,
        iconSvg: <OpenTableIcon />,
      });
    }
    // Add TripAdvisor if the business has a tripadvisor_link or is a hotel, restaurant, or golf course
    if (hasTripAdvisor) {
      options.push({
        text: 'TripAdvisor',
        id: 'tripadvisor' as ReportSource,
        iconSvg: <TripAdvisorIcon />,
      });
    }
    // Add Booking.com if the business has a booking_link or is a hotel
    if (hasBooking) {
      options.push({
        text: 'Booking.com',
        id: 'booking' as ReportSource,
        iconSvg: <BookingIcon />,
      });
    }
    // Add Expedia if the business has an expedia_link or is a hotel
    if (hasExpedia) {
      options.push({ text: 'Expedia', id: 'expedia' as ReportSource, iconSvg: <ExpediaIcon /> });
    }
    // Add GolfPass if the business has a golfpass_link or is a golf course
    if (hasGolfPass) {
      options.push({ text: 'GolfPass', id: 'golfpass' as ReportSource, iconSvg: <GolfPassIcon /> });
    }
    options.push({ text: 'AI Analysis', id: 'ai' as ReportSource, iconSvg: <AIIcon /> });
    return options;
  }, [
    hasDealerRater,
    hasCarsCom,
    hasCarFax,
    hasEdmunds,
    hasCarGurus,
    hasOpenTable,
    hasTripAdvisor,
    hasBooking,
    hasExpedia,
    hasGolfPass,
  ]);
  const ALL_RANGE_OPTIONS = CALENDAR_PERIOD_OPTIONS;

  const [selectedRange, setSelectedRange] = useQueryParam('range', RangeFiltersParam);
  const [selectedSource, setSelectedSource] = useQueryParam('source', SourceFiltersParam);

  const currentRange =
    ALL_RANGE_OPTIONS.find((option) => option.id === selectedRange) ?? ALL_RANGE_OPTIONS[0];

  const visibleSourceIds = useMemo(
    () => ALL_SOURCE_OPTIONS.map((option) => option.id),
    [ALL_SOURCE_OPTIONS],
  );

  const sourceAvailability = useMemo(
    () =>
      computeSourceAvailabilityMap({
        visibleSources: visibleSourceIds,
        periods: report.periods,
        tasks: report.tasks,
        issueGroups: report.issue_groups,
      }),
    [visibleSourceIds, report.periods, report.tasks, report.issue_groups],
  );

  const sourceOptions = useMemo<SourceOption[]>(() => {
    return ALL_SOURCE_OPTIONS.map((option) => {
      const availability = sourceAvailability[option.id];
      return {
        ...option,
        availability,
        disabled: availability.disabled,
        disabledReason: availability.disabledReason,
      };
    });
  }, [ALL_SOURCE_OPTIONS, sourceAvailability]);

  const activeSource = useMemo(() => {
    const requestedSource = sourceOptions.find((option) => option.id === selectedSource);
    if (requestedSource && !requestedSource.disabled) {
      return requestedSource;
    }
    return sourceOptions.find((option) => !option.disabled) ?? sourceOptions[0];
  }, [sourceOptions, selectedSource]);
  const activeSourceId = activeSource.id;
  const displaySource = activeSourceId === 'ai' ? 'google' : activeSourceId;

  useEffect(() => {
    if (selectedSource !== activeSourceId) {
      setSelectedSource(activeSourceId);
    }
  }, [activeSourceId, selectedSource, setSelectedSource]);

  const currentPeriodIdx = useMemo(() => {
    return report.periods.findIndex(
      (period) => period.range === selectedRange && periodMatchesSource(period, displaySource),
    );
  }, [report.periods, selectedRange, displaySource]);

  // Check if any source is still loading
  const anySourceLoading = useMemo(() => {
    return Object.values(sourceAvailability).some((source) => source.state === 'loading');
  }, [sourceAvailability]);

  const pollReport = useEffectEvent(() => {
    if (document.hidden || navigator.onLine === false || !onRefresh) {
      return;
    }

    void onRefresh().catch(() => undefined);
  });

  // Continue polling while any source is still loading
  useEffect(() => {
    if (!anySourceLoading) return;

    const pollInterval = setInterval(pollReport, 5000);

    return () => clearInterval(pollInterval);
  }, [anySourceLoading]);

  const currentSource =
    sourceOptions.find((option) => option.id === displaySource) ?? sourceOptions[0];

  const availableDownloadSources = useMemo<ReportSource[]>(() => {
    return sourceOptions
      .filter((option) => option.availability.state === 'data')
      .map((option) => option.id);
  }, [sourceOptions]);
  // TODO change to use report.report_at?
  const createdAt = new Date(report.created_at + 'Z');
  const rangeEnd = createdAt.toLocaleDateString();

  const period = currentPeriodIdx >= 0 ? report.periods[currentPeriodIdx] : undefined;
  const headerPeriod = useMemo(() => {
    return (
      period ??
      report.periods.find(
        (candidate) =>
          candidate.range === selectedRange && periodMatchesSource(candidate, 'google'),
      ) ??
      report.periods.find((candidate) => periodMatchesSource(candidate, 'google')) ??
      report.periods[0]!
    );
  }, [period, report.periods, selectedRange]);
  const placeLetterGrade = headerPeriod.place_letter_grade;
  const placePercentile = headerPeriod.place_percentile;

  const rangeOptions = useMemo(() => {
    const disabledOptions = ALL_RANGE_OPTIONS.map((option) => {
      const hasPeriod = report.periods.some(
        (period) =>
          period.range === option.id &&
          (periodMatchesSource(period, activeSourceId) ||
            (activeSourceId === 'ai' && !!report.issue_groups && option.id === '180-days')),
      );
      // TODO revert when google pipeline is fixed, but for now only show 30-days for google
      const isDisabled = !hasPeriod;
      // const isDisabled = !hasPeriod || (option.id !== '30-days' && selectedSource === 'google');
      return {
        ...option,
        disabled: isDisabled,
        disabledReason: isDisabled ? 'No data available' : undefined,
      };
    });
    return disabledOptions;
  }, [report.periods, activeSourceId, report.issue_groups]);

  useEffect(() => {
    if (
      activeSourceId === 'ai' &&
      !!report.issue_groups &&
      rangeOptions.some((option) => option.id === '180-days')
    ) {
      setSelectedRange('180-days');
    }
  }, [activeSourceId, report.issue_groups, rangeOptions, setSelectedRange]);
  // TODO revert when google pipeline is fixed, but for now only show 30-days for google
  // useEffect(() => {
  //   if (selectedSource === 'google' && rangeOptions.some((option) => option.id !== '30-days')) {
  //     setSelectedRange('30-days');
  //   }
  // }, [selectedSource, rangeOptions]);

  const borderColor = useMemo(() => {
    const grade = headerPeriod.place_letter_grade;
    if (grade.includes('A')) {
      return 'border-green-600';
    } else if (grade.includes('B')) {
      return 'border-green-400';
    } else if (grade.includes('C')) {
      return 'border-yellow-400';
    }
    // D and F are both red
    return 'border-red-500';
  }, [headerPeriod.place_letter_grade]);

  const opportunityPeriods = report.periods.filter(
    (candidate) => candidate.range === currentRange.id && candidate.source !== 'ai',
  );
  const headerOpportunities = getScorecardOpportunities(
    opportunityPeriods,
    rangeIdToDays[currentRange.id] ?? 30,
  );

  const content = (() => {
    if (!period) {
      return null;
    }

    return activeSourceId === 'ai' ? (
      <CardIssuesView
        report={report}
        period={period}
        currentRange={currentRange}
        currentSource={currentSource}
        issueGroups={report.issue_groups ?? []}
      />
    ) : (
      <CardPeriodView
        report={report}
        period={period}
        currentRange={currentRange}
        currentSource={currentSource}
      />
    );
  })();

  return (
    <>
      <Grid gridDefinition={[{ colspan: { default: 12 } }, { colspan: { default: 12 } }]}>
        <Container
          header={
            <Header
              variant="h1"
              description={`Created ${rangeEnd}`}
              actions={
                <SpaceBetween direction="horizontal" size="xs">
                  <Button iconName="send" onClick={() => setSendModalVisible(true)}>
                    Send
                  </Button>
                  <ReportDownloadButton
                    item={report}
                    period={currentRange.id}
                    variant="button"
                    sources={availableDownloadSources}
                    defaultSource="combined"
                    customerVolume={customerVolume}
                  />
                </SpaceBetween>
              }
            >
              Reputation Scorecard
            </Header>
          }
        >
          <div className="flex flex-col lg:flex-row lg:justify-between lg:items-stretch gap-4">
            <div className="flex min-w-0 flex-col order-1 lg:flex-1">
              <ScorecardHeader
                placeLetterGrade={placeLetterGrade}
                placePercentile={placePercentile}
                place={report.place}
                snapshotMap={snapshotMap}
                borderColor={borderColor}
                source={currentSource.id}
              />
              <CompetitorSection competitors={report.competitors ?? []} place={report.place} />
              <div className="mt-4 max-w-64">
                <FormField
                  label={`Monthly ${report.customer_term ?? 'customers'}`}
                  errorText={customerVolumeError}
                >
                  <Input
                    type="text"
                    inputMode="numeric"
                    value={customerVolumeInput}
                    onChange={({ detail }) => onCustomerVolumeChange(detail.value)}
                  />
                </FormField>
              </div>
              <div className="hidden lg:block mt-auto pt-2">
                <CalendarPeriodControl
                  selectedId={selectedRange}
                  onChange={setSelectedRange}
                  options={rangeOptions}
                />
              </div>
            </div>
            <div className="order-2 flex min-w-0 flex-col gap-4 lg:order-3 lg:w-[23rem] lg:shrink-0 xl:w-[28rem]">
              <SourceRatingsSummary
                place={report.place}
                snapshotMap={snapshotMap}
                showDealerRater={hasDealerRater}
                showCarsCom={hasCarsCom}
                showCarFax={hasCarFax}
                showEdmunds={hasEdmunds}
                showCarGurus={hasCarGurus}
                showOpenTable={hasOpenTable}
                showTripAdvisor={hasTripAdvisor}
                showBooking={hasBooking}
                showExpedia={hasExpedia}
                showGolfPass={hasGolfPass}
                onSourceChange={(source: string) => setSelectedSource(source)}
              />
              {headerOpportunities.length > 0 ? (
                <ScorecardOpportunities opportunities={headerOpportunities} />
              ) : null}
              <div className="hidden lg:flex lg:justify-end mt-auto pt-2">
                {period && (
                  <ViolationsSummaryBadge
                    reviews={report.reviews ?? []}
                    periodStartDate={period.start_date}
                    periodEndDate={period.end_date}
                    source={displaySource}
                  />
                )}
              </div>
            </div>
            <div className="order-3 lg:hidden">
              <CalendarPeriodControl
                selectedId={selectedRange}
                onChange={setSelectedRange}
                options={rangeOptions}
              />
              <div className="mt-2">
                {period && (
                  <ViolationsSummaryBadge
                    reviews={report.reviews ?? []}
                    periodStartDate={period.start_date}
                    periodEndDate={period.end_date}
                    source={displaySource}
                  />
                )}
              </div>
            </div>
          </div>
        </Container>
        <ScorecardSourceTabs
          activeTabId={activeSourceId}
          onSourceChange={(source) => setSelectedSource(source)}
          options={sourceOptions.map((option) => ({
            id: option.id,
            text: option.text,
            icon: option.iconSvg,
            availability: option.availability,
          }))}
          content={content}
        />
      </Grid>
      <PublicScorecardSendModal
        visible={sendModalVisible}
        reportUuid={report.uuid}
        reportName={report.place.name}
        customerVolume={customerVolume}
        onDismiss={() => setSendModalVisible(false)}
      />
    </>
  );
}

export type CardPeriodViewProps = {
  report: LoadedPlaceReport;
  period: PlacePeriodReport;
  currentRange: { text: string; id: string };
  currentSource: { text: string; id: string };
};

export function CardPeriodView(props: CardPeriodViewProps) {
  const report = props.report;
  const period = props.period;
  const currentRange = props.currentRange;
  const currentSource = props.currentSource;
  const reviewCount = period.review_count;
  const reviewCountStatus = period.review_count_status as StatisticStatus;
  const averageRating = period.average_rating;
  const averageRatingStatus = period.average_rating_status as StatisticStatus;
  const responseRate = period.response_rate;
  const responseRateStatus = period.response_rate_status as StatisticStatus;
  const avgResponseTime = period.avg_response_time;
  const avgResponseTimeStatus = period.avg_response_time_status as StatisticStatus;
  const hasNoReviews = reviewCount === 0;

  const rangeDays = rangeIdToDays[currentRange.id];
  const sourceSnapshot = report.source_snapshots?.find(
    (snapshot) => snapshot.source === currentSource.id,
  );
  const lifetimeRating =
    sourceSnapshot?.rating ?? getPlaceSourceData(report.place, currentSource.id).rating;
  const ratingTrend = hasNoReviews ? null : getRatingTrend(averageRating, lifetimeRating);

  const placeName = report.place.name;

  const filteredReviews = useMemo(() => {
    // only show reviews from the current period
    return (
      report.reviews?.filter((review) => {
        return (
          review.review_date &&
          review.review_date >= period.start_date &&
          review.review_date <= period.end_date &&
          review.source === period.source &&
          review.content
        );
      }) ?? []
    );
  }, [report.reviews, period]);

  return (
    <SpaceBetween direction="vertical" size="l">
      <SpaceBetween direction="vertical" size="l">
        <Header variant="h2">Past {currentRange?.text ?? '30 Days'}</Header>
        <Grid
          gridDefinition={[
            {
              colspan: {
                default: 12,
                xs: 3,
              },
            },
            {
              colspan: {
                default: 12,
                xs: 3,
              },
            },
            {
              colspan: {
                default: 12,
                xs: 3,
              },
            },
            {
              colspan: {
                default: 12,
                xs: 3,
              },
            },
          ]}
        >
          {/* TODO make sure numbers are formatted correctly */}
          <StatisticBox
            statistic={reviewCount.toLocaleString()}
            percentile={period.review_count_percentile}
            status={reviewCountStatus}
            details={`${((reviewCount / rangeDays) * 30).toFixed(0)} / month`}
            icon="contact"
            label="Reviews"
          />
          <StatisticBox
            statistic={hasNoReviews ? 'N/A' : averageRating.toFixed(2)}
            percentile={period.average_rating_percentile}
            status={averageRatingStatus}
            icon="star"
            label="Average Rating"
            trend={ratingTrend}
          />
          <StatisticBox
            statistic={hasNoReviews ? 'N/A' : (100 * responseRate).toFixed(0) + '%'}
            percentile={period.response_rate_percentile}
            status={responseRateStatus}
            icon="undo"
            label="Your Response Rate"
          />
          <StatisticBox
            statistic={hasNoReviews ? 'N/A' : avgResponseTime}
            percentile={period.avg_response_time_percentile}
            status={avgResponseTimeStatus}
            icon="calendar"
            label="Avg Response Time"
          />
        </Grid>
      </SpaceBetween>
      {period.keywords && period.keywords.length > 0 && (
        <KeywordList
          header={
            <Header
              variant="h2"
              description={`Frequently used keywords from reviews in the past ${currentRange?.text.toLowerCase() ?? '30 days'} on ${currentSource?.text ?? 'Google'}`}
            >
              Frequently Used Keywords
            </Header>
          }
          keywords={period.keywords ?? []}
          range={currentRange?.text.toLowerCase() ?? '30 days'}
        />
      )}
      {filteredReviews && filteredReviews.length > 0 && (
        <ReviewList
          reviews={filteredReviews}
          placeName={placeName}
          header={
            <Header
              variant="h2"
              description={`Negative reviews and their replies in the past ${currentRange?.text.toLowerCase() ?? '30 days'} on ${currentSource?.text ?? 'Google'}`}
            >
              Negative Reviews
            </Header>
          }
        />
      )}
    </SpaceBetween>
  );
}

export type CardIssuesViewProps = {
  report: LoadedPlaceReport;
  period: PlacePeriodReport;
  currentRange: { text: string; id: string };
  currentSource: { text: string; id: string };
  issueGroups: IssueGroup[];
};

export function CardIssuesView(props: CardIssuesViewProps) {
  const report = props.report;
  const period = props.period;
  const currentRange = props.currentRange;
  const currentSource = props.currentSource;
  const issueGroups = props.issueGroups;

  const issueCount = useMemo(() => {
    return issueGroups.reduce((acc, issueGroup) => acc + issueGroup.issues.length, 0);
  }, [issueGroups]);

  const issueGroupCount = useMemo(() => {
    return issueGroups.length;
  }, [issueGroups]);

  const issueNumberLookup = useMemo(() => {
    const issueNumberLookup = new Map<string, number>();
    for (const issueGroup of issueGroups) {
      for (const issue of issueGroup.issues) {
        issueNumberLookup.set(issue.uuid, issueNumberLookup.size + 1);
      }
    }
    return issueNumberLookup;
  }, [issueGroups]);

  return (
    <SpaceBetween direction="vertical" size="l">
      <Header
        variant="h2"
        description={`${issueCount.toLocaleString()} commonly encountered issues at ${report.place.name} across all sources in the past ${currentRange?.text.toLowerCase() ?? '30 days'}, selected by AI`}
      >
        Common Issues across {issueGroupCount.toLocaleString()} Issue Areas
      </Header>
      <SpaceBetween direction="vertical" size="xs">
        {issueGroups.map((issueGroup) => (
          <IssueGroupView
            key={issueGroup.uuid}
            issueGroup={issueGroup}
            placeName={report.place.name}
            issueNumberLookup={issueNumberLookup}
          />
        ))}
      </SpaceBetween>
    </SpaceBetween>
  );
}
